import { bbox as turfBbox } from '@turf/turf'
import type { Feature } from 'geojson'
import maplibregl from 'maplibre-gl'
import type { CommuneViewData } from '@/services/map/CommuneViewBoundsService'
import type { GeoData } from '@/models/interfaces/geo/GeoData'

const COMMUNES_GEOJSON_URL = '/data/communes_cameroun.geojson'

type CommuneProperties = Record<string, string | number | boolean | null | undefined>

let cachedCollection: GeoJSON.FeatureCollection | null = null

function normalizeCommuneName(name: string): string {
  return name.trim().toLowerCase()
}

async function getCommunesGeoJSON(): Promise<GeoJSON.FeatureCollection> {
  if (cachedCollection) {
    return cachedCollection
  }
  const response = await fetch(COMMUNES_GEOJSON_URL)
  if (!response.ok) {
    throw new Error(`Failed to load communes GeoJSON: ${response.status}`)
  }
  const json = (await response.json()) as GeoJSON.FeatureCollection
  // Deduplicate by cmn_code then cmn_titre to avoid multiple matches for the same commune.
  const seen = new Set<string>()
  const deduped = json.features.filter((f) => {
    const p = (f.properties ?? {}) as CommuneProperties
    const key = String(p['cmn_code'] ?? p['cmn_titre'] ?? p['name'] ?? '')
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  cachedCollection = { ...json, features: deduped }
  return cachedCollection
}

function featureMatchesName(props: CommuneProperties, normalized: string): boolean {
  const candidates = [
    props['cmn_titre'],
    props['cmn_code'],
    props['name'],
    props['nom'],
  ] as (string | number | boolean | null | undefined)[]
  return candidates.some(
    (v) => typeof v === 'string' && normalizeCommuneName(v) === normalized,
  )
}

/**
 * Finds a commune in the Cameroon communes GeoJSON by cmn_titre, cmn_code or name (case-insensitive).
 * Returns bounds and geometry for the commune view (mask + maxBounds).
 */
export async function getCommuneViewDataFromGeoJSON(
  communeName: string,
): Promise<CommuneViewData | null> {
  const normalized = normalizeCommuneName(communeName ?? '')
  if (!normalized) {
    return Promise.resolve(null)
  }

  const collection = await getCommunesGeoJSON()
  const features = collection.features ?? []
  const match = features.find((f) =>
    featureMatchesName((f.properties ?? {}) as CommuneProperties, normalized),
  )

  if (!match || !match.geometry) {
    return null
  }

  const geoJsonFeature: Feature = {
    type: 'Feature',
    properties: match.properties ?? {},
    geometry: match.geometry,
  }
  const [minLng, minLat, maxLng, maxLat] = turfBbox(geoJsonFeature)
  const bounds = new maplibregl.LngLatBounds([minLng, minLat], [maxLng, maxLat])
  const geometry = match.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon
  return { bounds, geometry }
}

/**
 * Builds a GeoData from the GeoJSON feature matching communeName (cmn_titre / cmn_code / name).
 * Used to restore mapSearch from URL query parameters for any commune in the GeoJSON.
 */
export async function getGeoDataFromGeoJSON(communeName: string): Promise<GeoData | null> {
  const normalized = normalizeCommuneName(communeName ?? '')
  if (!normalized) return null

  const collection = await getCommunesGeoJSON()
  const features = collection.features ?? []
  const match = features.find((f) =>
    featureMatchesName((f.properties ?? {}) as CommuneProperties, normalized),
  )
  if (!match) return null

  const props = (match.properties ?? {}) as CommuneProperties
  const lat = parseFloat(String(props['cmn_lat'] ?? ''))
  const lng = parseFloat(String(props['cmn_long'] ?? ''))
  const title = String(props['cmn_titre'] ?? communeName)
  return {
    name: title,
    osmId: null,
    osmType: null,
    latitude: isFinite(lat) ? lat : undefined,
    longitude: isFinite(lng) ? lng : undefined,
    coords: isFinite(lng) && isFinite(lat) ? { lng, lat } : undefined,
  }
}
